import type { StepEvent } from './schemas/attempt.js';
import type { MasteryThreshold } from './schemas/chapter.js';

export type MasteryEvaluation = {
  totalSteps: number;
  scoredSteps: number;
  firstAttemptCorrect: number;
  selfCorrected: number;
  feedbackRecovered: number;
  wrong: number;
  /** True once every step is scored and the thresholds are met. */
  passed: boolean;
  /** True if passing is still mathematically reachable given steps left. */
  stillPassable: boolean;
};

/**
 * Only the first recorded event per step counts — repeated attempts on the
 * same step (however many) don't change its scored outcome once recorded.
 */
export function evaluateMastery(
  events: StepEvent[],
  totalSteps: number,
  threshold: MasteryThreshold,
): MasteryEvaluation {
  const firstOutcomeByStep = new Map<string, StepEvent['outcome']>();
  for (const ev of events) {
    if (!firstOutcomeByStep.has(ev.stepId)) firstOutcomeByStep.set(ev.stepId, ev.outcome);
  }
  const outcomes = [...firstOutcomeByStep.values()];
  const firstAttemptCorrect = outcomes.filter((o) => o === 'correct_first_try').length;
  const selfCorrected = outcomes.filter((o) => o === 'self_corrected').length;
  const feedbackRecovered = outcomes.filter((o) => o === 'feedback_recovered').length;
  const wrong = outcomes.filter((o) => o === 'wrong').length;
  const scoredSteps = outcomes.length;
  const remaining = Math.max(0, totalSteps - scoredSteps);

  // "wrong" is a harder failure than feedback_recovered (it exceeded the
  // per-step mistake budget), but both count against the same tolerance —
  // neither was resolved within a single self-correction.
  const uncorrected = feedbackRecovered + wrong;

  const fraction = totalSteps > 0 ? firstAttemptCorrect / totalSteps : 1;
  const bestPossibleFraction = totalSteps > 0 ? (firstAttemptCorrect + remaining) / totalSteps : 1;

  const thresholdsMet =
    selfCorrected <= threshold.maxSelfCorrectedMistakes && uncorrected <= threshold.maxUncorrectedMistakes;

  const passed = scoredSteps >= totalSteps && fraction >= threshold.minFirstAttemptCorrect && thresholdsMet;
  const stillPassable = bestPossibleFraction >= threshold.minFirstAttemptCorrect && thresholdsMet;

  return {
    totalSteps,
    scoredSteps,
    firstAttemptCorrect,
    selfCorrected,
    feedbackRecovered,
    wrong,
    passed,
    stillPassable,
  };
}
